import type { ConfirmationCategory, FulfillmentOrderLine } from "../types";
import { normalizeFulfillmentResourceType } from "../types";
import { isFulfillmentStayResourceLinked, pricingUnitTypeLabel, sumFulfillmentAmounts } from "./fulfillmentOrderEditorService";
import { feeAdjustmentSpecUnit } from "./structuredFeeAdjustment";
import { parseConfirmationDayExpression } from "./confirmationEntryParser";

export const CONFIRMATION_CATEGORIES = [
  { key: "stay", label: "住宿" }, { key: "train", label: "火车" },
  { key: "air", label: "飞机" }, { key: "ship", label: "船" }, { key: "bus", label: "大巴车" },
  { key: "ticket", label: "门票" }, { key: "meal", label: "餐" },
  { key: "crew", label: "司导" }, { key: "misc", label: "杂费" },
] as const;
export type ConfirmationViewCategory = ConfirmationCategory;

/** 展示名沿真实 SPU 与分类录入生成的业务快照回退，无需另维护项目标题。 */
export function confirmationLineDisplayName(line: Partial<FulfillmentOrderLine>): string {
  return line.spuNameSnapshot?.trim() || line.itemNameSnapshot?.trim() || line.fulfillmentSupplierName?.trim() || line.resourceType || "费用";
}

/** 主体齐全即可归档展示；住宿真实资源承担服务方，协议关联保留历史归档事实。 */
export function isConfirmationLineArchived(line: FulfillmentOrderLine): boolean {
  if (line.fulfillmentOrderId) return true;
  const hasService = isFulfillmentStayResourceLinked(line)
    || Boolean(line.fulfillmentSupplierId?.trim() && line.fulfillmentSupplierName?.trim());
  return hasService && Boolean(line.settlementSupplierId?.trim());
}

/** 仅按结构化资源身份识别展示分类。 */
export function confirmationCategoryForLine(line: Partial<FulfillmentOrderLine>): ConfirmationViewCategory | null {
  const type = normalizeFulfillmentResourceType(line.resourceType || "");
  const direct: Record<string, ConfirmationViewCategory> = { 住宿: "stay", 火车: "train", 飞机: "air", 包车: "bus", 大巴车: "bus", 考察点: "ticket", 餐: "meal", 导游: "crew", 司导: "crew", 杂费: "misc" };
  if (direct[type]) return direct[type];
  if (type !== "大交通") return null;
  if (line.transportMode === "ship" || line.procurementSourceType === "manual_ship") return "ship";
  if (line.resourceSelection?.kind === "transport") return line.resourceSelection.transportMode === "air" ? "air" : "train";
  if (line.transportMode === "rail") return "train";
  if (line.transportMode === "air") return "air";
  const tables = [line.spuTableName, line.skuTableName].filter(Boolean).join(" ").toLowerCase();
  if (/(?:^|[_\s])(air|flight|airplane)(?:[_\s]|$)/.test(tables)) return "air";
  if (/(?:^|[_\s])(train|rail|railway)(?:[_\s]|$)/.test(tables)) return "train";
  return null;
}

/** 只认可完整计费快照，历史自由规格不能被当成期间单位。 */
function confirmationCostSpecPeriod(line: Partial<FulfillmentOrderLine>): string | null {
  const category = confirmationCategoryForLine(line);
  if (category === "crew" || category === "misc") {
    const spec = line.itemSpecSnapshot?.normalize("NFKC").replace(/\s/gu, "").replace(/[xX*]/gu, "×") || "";
    const periods: Record<string, string> = { "人×天": "天", "人×晚": "晚", "人×次": "次", "项×次": "次" };
    if (Object.hasOwn(periods, spec)) return periods[spec];
  }
  return null;
}

/** 独立页列表与预览共用期间判断，未匹配规范快照时保留原资源回退。 */
export function confirmationBillingCountUnit(line: Partial<FulfillmentOrderLine>): string {
  return confirmationCostSpecPeriod(line) || feeAdjustmentSpecUnit(line.resourceType || "");
}

/** 司导的规范人数快照显示实际天、晚、次，存储类型和历史标签保持原值。 */
export function confirmationCrewPricingUnitLabel(line: FulfillmentOrderLine): string {
  const period = confirmationCostSpecPeriod(line);
  return confirmationCategoryForLine(line) === "crew" && line.unit === "人" && period
    ? `按人${period}计价`
    : pricingUnitTypeLabel(line.pricingUnitType);
}

/** 多类筛选保持确认行身份与未知类别。 */
export function confirmationVisibleLines(lines: FulfillmentOrderLine[], categories: ReadonlySet<ConfirmationViewCategory>): FulfillmentOrderLine[] {
  return lines.filter((line) => line.status === "active" && (!categories.size || categories.has(confirmationCategoryForLine(line)!)));
}

/** 服务日期身份独立于显示名称，避免跨次到访合并。 */
export function confirmationDateKey(line: FulfillmentOrderLine): string {
  return JSON.stringify([line.serviceDates?.length ? line.serviceDates : [line.serviceDateFrom, line.serviceDateTo], line.dayExpression || line.dayNumbers || null]);
}

/** 根据明确资源与服务范围构造展示分组。 */
export function confirmationGroupKey(line: FulfillmentOrderLine, category: ConfirmationViewCategory): string {
  const date = confirmationDateKey(line);
  const selection = line.resourceSelection;
  const name = line.spuNameSnapshot || line.itemNameSnapshot;
  if (category === "stay") return JSON.stringify([category, date]);
  if (category === "air" || category === "ship") return JSON.stringify([category, selection?.kind === "transport" ? [selection.fromPointId, selection.toPointId] : line.spuId || name.normalize("NFKC").trim().replace(/[‐‑–—−]/g, "-").replace(/\s*-\s*/g, "-")]);
  if (category === "ticket") return JSON.stringify([category, date, selection?.kind === "point" ? selection.pointSpuId : line.spuId || name]);
  if (category === "bus") return JSON.stringify([category, date, line.spuId || name, selection?.kind === "bus" ? selection.entrySemantic : line.itemSpecSnapshot]);
  if (category === "meal") return JSON.stringify([category, date, line.mealType || name]);
  return line.id;
}

/** 分类合并先归拢同组，再按酒店归拢；不修改原数组或底层行。 */
export function confirmationOrderedLines(lines: FulfillmentOrderLine[], category: ConfirmationViewCategory | null): FulfillmentOrderLine[] {
  if (!category || !["stay", "air", "ship", "bus", "ticket", "meal"].includes(category)) return lines;
  const groups = new Map<string, FulfillmentOrderLine[]>();
  for (const line of lines) {
    const key = confirmationGroupKey(line, category);
    groups.set(key, [...(groups.get(key) ?? []), line]);
  }
  const ordered = [...groups.values()];
  if (category === "meal" || category === "ticket") {
    const mealOrder: Record<string, number> = { breakfast: 0, 早餐: 0, 早: 0, lunch: 1, 午餐: 1, 中餐: 1, 中: 1, dinner: 2, 晚餐: 2, 晚: 2 };
    const firstDay = (line: FulfillmentOrderLine) => {
      const days = line.dayNumbers?.length ? line.dayNumbers : parseConfirmationDayExpression(line.dayExpression || "")?.numbers;
      return days?.length ? Math.min(...days) : Number.MAX_SAFE_INTEGER;
    };
    ordered.sort((a, b) => {
      const aDate = a[0].serviceDateFrom ?? a[0].serviceDates?.[0] ?? "";
      const bDate = b[0].serviceDateFrom ?? b[0].serviceDates?.[0] ?? "";
      return aDate.localeCompare(bDate)
        || (category === "meal" && !aDate && !bDate ? firstDay(a[0]) - firstDay(b[0]) : 0)
        || (category === "meal" ? (mealOrder[a[0].mealType ?? ""] ?? 3) - (mealOrder[b[0].mealType ?? ""] ?? 3) : 0)
        || a[0].lineNo - b[0].lineNo;
    });
  }
  return ordered.flatMap((group) => {
    if (category !== "stay") return group;
    const hotels = new Map<string, FulfillmentOrderLine[]>();
    for (const line of group) {
      const key = confirmationHotelKey(line);
      hotels.set(key, [...(hotels.get(key) ?? []), line]);
    }
    return [...hotels.values()].flat();
  });
}

/** 酒店资源身份优先，名称只用于兼容已冻结的历史快照。 */
export function confirmationHotelKey(line: FulfillmentOrderLine): string {
  return line.resourceSelection?.kind === "stay" ? line.resourceSelection.staySpuId : line.spuId || line.fulfillmentSupplierId || line.spuNameSnapshot || line.fulfillmentSupplierName || line.id;
}

/** 汇总当前传入的可见明细，不使用隐藏原始组的金额。 */
export function confirmationVisibleTotal(lines: FulfillmentOrderLine[]): string { return sumFulfillmentAmounts(lines.map((line) => line.amount)); }
